import { Link, useNavigate } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { img, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const StatusBadge = ({ status }) => {
  const map = { Available: "border-[#a855f7]/50 text-[#e9ddfb]", Reserved: "border-amber-400/60 text-amber-300", Sold: "border-emerald-400/60 text-emerald-300" };
  return <span className={`font-mono-j rounded-full border bg-[#07030c]/70 px-2.5 py-1 text-[11px] uppercase tracking-widest backdrop-blur ${map[status] || map.Available}`}>{status}</span>;
};

export const SaveButton = ({ pid, className = "" }) => {
  const { user, toggleSave } = useAuth();
  const nav = useNavigate();
  const saved = !!user?.saved?.includes(pid);
  const click = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return nav("/login");
    const now = await toggleSave(pid);
    toast(now ? "Saved to your wishlist" : "Removed from wishlist");
    return undefined;
  };
  return (
    <button onClick={click} data-testid={`save-button-${pid}`} aria-label="Save item" className={`grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-[#07030c]/60 backdrop-blur transition-transform duration-300 hover:scale-110 ${className}`}>
      <Heart className={`h-4 w-4 ${saved ? "fill-[#a855f7] text-[#a855f7]" : "text-white"}`} />
    </button>
  );
};

const track = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

export const ProductCard = ({ p }) => (
  <Link to={`/item/${p.id}`} onMouseMove={track} data-testid={`product-card-${p.id}`} className="group relative block">
    <div className="chamfer relative aspect-[4/5] overflow-hidden bg-[#110720]">
      <img src={img(p.images?.[0])} alt={p.title} loading="lazy" className={`h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110 ${p.status === "Sold" ? "grayscale" : ""}`} />
      <div className="spotlight absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#07030c]/90 to-transparent" />
      <div className="absolute left-4 top-4"><StatusBadge status={p.status} /></div>
      <SaveButton pid={p.id} className="absolute right-4 top-4" />
      {p.size && <span className="font-mono-j absolute bottom-4 left-4 rounded-full bg-[#f3eefc] px-3 py-1 text-xs font-medium text-[#07030c]">{p.size}</span>}
    </div>
    <div className="mt-4 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="truncate text-[15px] text-[#f3eefc] transition-colors group-hover:text-[#d8b4fe]">{p.title}</p>
        {p.review_count > 0 && (
          <p className="font-mono-j mt-1 flex items-center gap-1 text-xs text-[#a78bce]" data-testid={`review-stars-${p.id}`}>
            <Star className="h-3 w-3 fill-[#d8b4fe] text-[#d8b4fe]" /> {p.review_avg} ({p.review_count})
          </p>
        )}
      </div>
      <p className="font-mono-j text-base text-[#f3eefc]" data-testid={`product-price-${p.id}`}>{money(p.price)}</p>
    </div>
  </Link>
);
